"use client";
import React from "react";
import Image from "next/image";
import parse from "html-react-parser";
import ExpandableBlock from "@/components/ContentBlock/variants/ExpandableBlock";
import OCPromoBanner from "@/components/CourseContent/ContentRefresh/OCPromoBanner";
import VideoPlayer from "@/components/VideoPlayer";
import { Props } from "./types";
import Sponsors from "../Sponsors";
import Collaborators from "../Collaborators";
import useTranslations from "@/i18n/useTranslations";

export function ContentOverview({
  video,
  fullDescription,
  title = "About this course",
  courseData,
  promoBannerData = null,
  isVideoInSideRail = false,
}: Props) {
  const t = useTranslations("Common");
  const tCourse = useTranslations("Course");
  const resolvedTitle =
    !title || title === "About this course"
      ? tCourse("aboutThisCourse")
      : title;
  const styledFullDescription = fullDescription
    .replace(/<p>/g, '<p class="text-base md:text-lg text-gray-800 font-sans">')
    .replace(/<ul>/g, '<ul class="list-disc pl-4 pb-4">')
    .replace(
      /<li>/g,
      '<li class="ml-4 list-outside text-base md:text-lg text-gray-800 font-sans">',
    )
    .replace(/<a /g, '<a class="text-info-500 underline break-all"');

  const sponsors = courseData?.sponsors?.length ? courseData.sponsors : [];
  const collaborators = courseData?.collaborators?.length
    ? courseData.collaborators
    : [];

  const shouldShowPromoBanner =
    promoBannerData && courseData?.activeCourseRun?.isEnrollable;

  const splitColumns =
    video?.src || shouldShowPromoBanner || courseData?.productImageUrl;

  return (
    <div
      className={`relative grid grid-cols-1 ${
        splitColumns
          ? `${isVideoInSideRail ? "xl:grid-cols-1 lg:grid-cols-2" : "lg:grid-cols-2"}`
          : "lg:grid-cols-1"
      } gap-y-12 lg:gap-12`}
    >
      <div className="flex flex-col gap-4">
        {resolvedTitle && (
          <h2 className="md:text-3xl font-bold mt-0 mb-0">{resolvedTitle}</h2>
        )}
        <ExpandableBlock
          blur
          btnClassName="text-gray-800 underline-offset-4 mt-4"
          blurClassName="from-putty-100 via-putty-100/80"
          className="max-w-full"
        >
          <div className="text-gray-800">{parse(styledFullDescription)}</div>
          <div className="hidden md:block">
            {sponsors?.length > 0 && sponsors[0] != null ? (
              <Sponsors
                sponsors={sponsors}
                containerClassName="flex flex-col gap-4"
                titleClassName="m-0"
              />
            ) : null}
            {collaborators?.length > 0 && collaborators[0] != null ? (
              <Collaborators
                containerClassName="flex flex-col gap-4"
                titleClassName="m-0"
                collaborators={collaborators}
              />
            ) : null}
          </div>
        </ExpandableBlock>
      </div>

      {shouldShowPromoBanner && courseData?.activeCourseRun?.isEnrollable && (
        <OCPromoBanner
          course={courseData}
          courseRun={courseData.activeCourseRun}
          data={promoBannerData}
        />
      )}

      {!shouldShowPromoBanner && video?.src && (
        <div
          className={`w-full flex justify-center items-start ${isVideoInSideRail ? "hidden md:flex xl:hidden" : ""}`}
        >
          <div className="w-full max-h-[40rem] flex justify-center">
            <VideoPlayer
              videoUrl={video.src}
              title={t("courseVideo")}
              videoThumbnail={video.image?.src}
              videoContainerClassName="relative w-full h-full flex items-center justify-center"
              videoClassName="w-full h-full"
              thumbnailClassName="object-cover w-full h-full max-h-none max-w-none"
              thumbnailWidth={400}
              thumbnailHeight={225}
            />
          </div>
        </div>
      )}

      {!shouldShowPromoBanner && !video?.src && courseData?.productImageUrl && (
        <div
          className={`w-full flex justify-center items-start ${isVideoInSideRail ? "hidden md:flex xl:hidden" : ""}`}
        >
          <div className="w-full max-h-[40rem] flex justify-center">
            <Image
              width={400}
              height={225}
              src={courseData.productImageUrl}
              alt={courseData.productName || t("courseImage")}
              className="object-cover w-full h-full max-h-none max-w-none"
              style={{ maxHeight: "40rem" }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ContentOverview;
